import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FiEye, FiEdit2, FiCheck, FiX } from "react-icons/fi";
import AdminDocumentUploader from "../AdminDocumentUploader";
import AdminDocumentViewer from "../AdminDocumentViewer";
import { CheckCircle, XCircle, Clock, Eye, AlertCircle, FileText, Calendar, Globe, MapPin, Search, Edit, Plus, Check, X, Trash2, MessageSquare } from "lucide-react";
import AdminDocumentModal from "../AdminDocumentModal";
import AdminCustomVisaModal from "./AdminCustomVisaModal";
import EditVisaModal from "../visa/EditVisaModal";

const AdminVisaApplications = () => {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [remark, setRemark] = useState("");
  const [actionType, setActionType] = useState(""); // 'approved' or 'rejected'
  const [uploading, setUploading] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const url = import.meta.env.VITE_URL || "http://localhost:3000";

  const [selectedUserForModal, setSelectedUserForModal] = useState(null);

  useEffect(() => {
    fetchVisaApplications();
  }, []);

  const fetchVisaApplications = async () => {
    try {
      const res = await axios.get(`${url}/api/visa/admin/applications`, {
        withCredentials: true,
      });
      if (res.data.success) {
        setVisas(res.data.applications);
      }
    } catch (error) {
      toast.error("Failed to fetch visa applications");
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (visa, action) => {
    setSelectedVisa(visa);
    setActionType(action);
    setRemark(visa.adminRemark || "");
    setShowModal(true);
  };

  const submitAction = async () => {
    try {
      const payload = actionType === 'remark' 
        ? { status: selectedVisa.status, adminRemark: remark } 
        : { status: actionType, adminRemark: remark };
        
      const res = await axios.patch(
        `${url}/api/visa/admin/applications/${selectedVisa._id}`,
        payload,
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(actionType === 'remark' ? "Remark saved successfully" : `Application ${actionType} successfully`);
        setShowModal(false);
        fetchVisaApplications(); // refresh
      }
    } catch (error) {
      toast.error(actionType === 'remark' ? "Failed to save remark" : "Failed to update status");
    }
  };

  const handleDeleteApplication = async (id) => {
    if (!window.confirm("Are you sure you want to delete this visa application? This action cannot be undone.")) {
      return;
    }
    setDeletingId(id);
    try {
      const res = await axios.delete(`${url}/api/visa/admin/applications/${id}`, {
        withCredentials: true,
      });
      if (res.data.success) {
        toast.success(res.data.message || "Visa application deleted successfully");
        setVisas((prevVisas) => prevVisas.filter((v) => v._id !== id));
      } else {
        toast.error(res.data.message || "Failed to delete application");
      }
    } catch (error) {
      console.error("Error deleting visa application:", error);
      toast.error(error.response?.data?.message || "Failed to delete application");
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedVisa) return;
    const formData = new FormData();
    formData.append("eTicket", file);
    setUploading(true);
    try {
      const res = await axios.put(
        `${url}/api/visa/admin/applications/${selectedVisa._id}/e-ticket`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      if (res.data.success) {
        toast.success("Document uploaded successfully");
        // Update local state to reflect the new document
        setSelectedVisa({ ...selectedVisa, eTicketPdfPath: res.data.data.eTicketPdfPath });
        fetchVisaApplications(); // Refresh list to get updated data
      } else {
        toast.error(res.data.error || "Failed to upload document");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const getDocUrl = (filename) => {
    if (!filename) return "#";
    return `${url}/uploads/visaApplications/${filename}`;
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Visa Applications</h1>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <button
            onClick={() => window.location.href = "/admin/visa-applications/create"}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            <Plus size={18} /> Create Application
          </button>
          {/* <button
            onClick={() => setShowCustomModal(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            <Plus size={18} /> Custom Application
          </button> */}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : visas.length === 0 ? (
        <div className="text-center py-10 text-gray-500">No applications found</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-4 border-b">User / Applicant</th>
                <th className="p-4 border-b">Country & Visa</th>
                <th className="p-4 border-b">Date</th>
                <th className="p-4 border-b">Status</th>
                <th className="p-4 border-b text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visas.map((visa) => (
                <tr key={visa._id} className="hover:bg-gray-50">
                  <td className="p-4 border-b">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{visa.primaryApplicant?.fullName}</p>
                      {visa.user && (
                        <button
                          onClick={() => setSelectedUserForModal(visa.user)}
                          className="text-blue-500 hover:text-blue-700"
                          title="View User Details"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{visa.user?.email || "No Email"}</p>
                    <p className="text-xs text-gray-500">{visa.user?.phone || "No Mobile"}</p>
                  </td>
                  <td className="p-4 border-b">
                    <p className="font-medium">{visa.visaDetails?.country}</p>
                    <p className="text-xs text-gray-500">{visa.visaDetails?.visaType}</p>
                  </td>
                  <td className="p-4 border-b text-sm">
                    {new Date(visa.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric"
                    })}
                  </td>
                  <td className="p-4 border-b">
                    <span className={`px-2 py-1 text-xs rounded-full ${visa.status === 'approved' ? 'bg-green-100 text-green-700' :
                      visa.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                      {visa.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4 border-b text-center space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => handleActionClick(visa, 'view')}
                      className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200" title="View Details">
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => setEditingApp(visa)}
                      className="p-1.5 bg-indigo-100 text-indigo-600 rounded hover:bg-indigo-200" title="Edit Application">
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleActionClick(visa, 'remark')}
                      className="p-1.5 bg-orange-100 text-orange-600 rounded hover:bg-orange-200" title="Admin Remark">
                      <MessageSquare size={16} />
                    </button>
                    {visa.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleActionClick(visa, 'approved')}
                          className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200" title="Approve">
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => handleActionClick(visa, 'rejected')}
                          className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200" title="Reject">
                          <X size={16} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteApplication(visa._id)}
                      disabled={deletingId === visa._id}
                      className={`p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors ${
                        deletingId === visa._id ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      title="Delete Application"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ACTION MODAL */}
      {showModal && selectedVisa && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {actionType === 'view' ? 'Visa Application Details' :
                  actionType === 'remark' ? 'Admin Remark' :
                  actionType === 'approved' ? 'Approve Application' : 'Reject Application'}
              </h2>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm bg-gray-50 p-4 rounded border">
              <div>
                <p className="font-semibold text-gray-500">Primary Applicant</p>
                <p>{selectedVisa.primaryApplicant?.fullName}</p>
                <p>Passport: {selectedVisa.primaryApplicant?.passportNumber}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-500">Destination</p>
                <p>{selectedVisa.visaDetails?.country} - {selectedVisa.visaDetails?.visaType}</p>
              </div>
            </div>

            <div className="mb-4 max-h-[40vh] overflow-y-auto pr-2">
              <h3 className="font-semibold mb-2">Primary Applicant Documents</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {Array.isArray(selectedVisa.documents) ? (
                  selectedVisa.documents.map((doc, idx) => (
                    <a key={idx} href={getDocUrl(doc.fileUrl)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 text-xs text-blue-600 truncate">
                      <FileText size={14} />
                      {doc.docName}
                    </a>
                  ))
                ) : (
                  Object.entries(selectedVisa.documents || {}).map(([key, value]) => {
                    if (!value) return null;
                    return (
                      <a key={key} href={getDocUrl(value)} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 text-xs text-blue-600 truncate">
                        <FileText size={14} />
                        {key}
                      </a>
                    );
                  })
                )}
              </div>

              {selectedVisa.familyMembers?.length > 0 && (
                <>
                  <h3 className="font-semibold mb-2 mt-4 border-t pt-4">Family Members</h3>
                  <div className="space-y-4">
                    {selectedVisa.familyMembers.map((member, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded border">
                        <p className="font-medium text-sm">{member.fullName} <span className="font-normal text-gray-500">(Passport: {member.passportNumber})</span></p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                          {Array.isArray(member.documents) ? (
                            member.documents.map((doc, dIdx) => (
                              <a key={dIdx} href={getDocUrl(doc.fileUrl)} target="_blank" rel="noreferrer"
                                className="flex items-center gap-2 p-2 bg-white border rounded hover:bg-gray-50 text-xs text-blue-600 truncate">
                                <FileText size={14} />
                                {doc.docName}
                              </a>
                            ))
                          ) : (
                            Object.entries(member.documents || {}).map(([docKey, docValue]) => {
                              if (!docValue) return null;
                              return (
                                <a key={docKey} href={getDocUrl(docValue)} target="_blank" rel="noreferrer"
                                  className="flex items-center gap-2 p-2 bg-white border rounded hover:bg-gray-50 text-xs text-blue-600 truncate">
                                  <FileText size={14} />
                                  {docKey}
                                </a>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {selectedVisa && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="pt-2">
                  <AdminDocumentViewer adminDocuments={selectedVisa.adminDocuments} />
                  <AdminDocumentUploader
                    type="visa"
                    bookingId={selectedVisa._id}
                    onUploadSuccess={(updatedDocs) => {
                      setSelectedVisa({ ...selectedVisa, adminDocuments: updatedDocs });
                      fetchApplications();
                    }}
                  />
                </div>
              </div>
            )}

            {actionType !== 'view' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {actionType === 'remark' ? 'Admin Remark (Only visible to you)' : 'Remark (Optional for Approve, Recommended for Reject)'}
                </label>
                <textarea
                  className="w-full border rounded-lg p-2 text-sm"
                  rows="3"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder={actionType === 'remark' ? "Add your remark here..." : "E.g., Passport copy is blurry..."}
                />
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">
                Close
              </button>
              {actionType !== 'view' && (
                <button
                  onClick={submitAction}
                  className={`px-4 py-2 text-white rounded ${actionType === 'approved' ? 'bg-green-600 hover:bg-green-700' : actionType === 'remark' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {actionType === 'remark' ? 'Save Remark' : `Confirm ${actionType === 'approved' ? 'Approval' : 'Rejection'}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* User Details Modal */}
      {selectedUserForModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-800">User Details</h3>
              <button onClick={() => setSelectedUserForModal(null)} className="text-gray-500 hover:bg-gray-200 p-2 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 mb-1">Username</p>
                  <p className="font-medium text-gray-900">{selectedUserForModal.username || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-gray-900">{selectedUserForModal.email || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Phone</p>
                  <p className="font-medium text-gray-900">{selectedUserForModal.phone || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Gender</p>
                  <p className="font-medium text-gray-900">{selectedUserForModal.gender || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Hometown</p>
                  <p className="font-medium text-gray-900">{selectedUserForModal.hometowncity || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Current City</p>
                  <p className="font-medium text-gray-900">{selectedUserForModal.city || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Date of Birth</p>
                  <p className="font-medium text-gray-900">
                    {selectedUserForModal.DOB ? new Date(selectedUserForModal.DOB).toLocaleDateString() : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Verification Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${selectedUserForModal.isVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {selectedUserForModal.isVerified ? "Verified" : "Unverified"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingApp && (
        <EditVisaModal
          application={editingApp}
          url={url}
          onClose={() => setEditingApp(null)}
          onSuccess={(updatedApp) => {
            setVisas(visas.map(app => app._id === updatedApp._id ? updatedApp : app));
            setEditingApp(null);
          }}
        />
      )}
      {showCustomModal && (
        <AdminCustomVisaModal
          onClose={() => setShowCustomModal(false)}
          onCreated={() => {
            fetchVisaApplications();
            setShowCustomModal(false);
          }}
        />
      )}
    </div>
  );
};

export default AdminVisaApplications;
